import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatDateTime, ageFromBirthDate } from "../lib/format";
import { Contact, Search, History, ArrowRight, Loader2, Pencil, Trash2, FileText } from "lucide-react";
import { StatoBadge, eur } from "./PreventiviPage";
import { formatApiErrorDetail } from "../lib/api";
import { validateCodiceFiscale } from "../lib/codiceFiscale";

const patientSubtitle = (p) =>
  [p.nato_a && `Nato a ${p.nato_a}`, p.nato_il && `il ${p.nato_il}`, p.indirizzo].filter(Boolean).join(" · ");

const fmtDate = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");

const PatientPreventivi = ({ items, onOpen }) => (
  <div data-testid="patient-preventivi">
    <p className="nx-label mb-2">Preventivi</p>
    {items.length === 0 ? (
      <p className="text-sm text-slate-500 py-3 text-center">Nessun preventivo collegato a questo paziente.</p>
    ) : (
      <div className="space-y-2">
        {items.map((pv) => (
          <button key={pv.id} data-testid={`patient-preventivo-${pv.id}`} onClick={() => onOpen(pv.id)}
            className="w-full text-left rounded-md border border-slate-200 px-4 py-3 hover:bg-slate-50 transition-colors flex flex-wrap items-center gap-3">
            <FileText className="h-4 w-4 text-primary" />
            <span className="font-mono text-xs font-bold text-primary w-28">{pv.codice}</span>
            <span className="text-xs text-slate-500 flex-1">Preventivo del {fmtDate(pv.data)}{pv.data_trasporto && ` · trasporto ${fmtDate(pv.data_trasporto)}`}</span>
            <span className="text-sm font-semibold text-slate-700">{pv.totale != null ? eur(pv.totale) : "—"}</span>
            <StatoBadge stato={pv.stato} />
          </button>
        ))}
      </div>
    )}
  </div>
);

export default function PatientsPage() {
  const { user } = useAuth();
  const canEdit = user?.staff_role !== "operatore";
  const navigate = useNavigate();
  const [patients, setPatients] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(null);
  const [preventivi, setPreventivi] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get("paziente");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ nominativo: "", anni: "", peso: "", telefono: "", indirizzo: "", nato_a: "", nato_il: "", codice_fiscale: "", email: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (q = "") => {
    setPatients(null);
    try {
      const { data } = await api.get("/patients", { params: q ? { q } : {} });
      setPatients(data);
    } catch {
      setPatients([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query, load]);

  const openHistory = async (p) => {
    setSelected(p);
    setHistory(null);
    setPreventivi([]);
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/patients/${p.id}/history`);
      setSelected((s) => ({ ...data.patient, ...s }));
      setHistory(data.history || []);
      setPreventivi(data.preventivi || []);
    } catch {
      setHistory([]);
      toast.error("Paziente non trovato");
    } finally { setLoadingHistory(false); }
  };

  useEffect(() => {
    if (deepLinkId) { openHistory({ id: deepLinkId }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId]);

  const closeHistory = () => {
    setSelected(null); setHistory(null); setPreventivi([]);
    if (deepLinkId) setSearchParams({}, { replace: true });
  };

  const openEdit = (p) => {
    setEditForm({ nominativo: p.nominativo || "", anni: p.anni || "", peso: p.peso || "", telefono: p.telefono || "", indirizzo: p.indirizzo || "", nato_a: p.nato_a || "", nato_il: p.nato_il || "", codice_fiscale: p.codice_fiscale || "", email: p.email || "" });
    setEditing(p);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/patients/${editing.id}`, editForm);
      toast.success("Paziente aggiornato", { description: "Le richieste collegate sono state allineate." });
      setEditing(null);
      await load(query.trim());
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  const removePatient = async (p) => {
    if (!window.confirm(`Eliminare il paziente "${p.nominativo}" dall'anagrafica?\n\nNon verranno eliminate le richieste di trasporto collegate.`)) return;
    try {
      await api.delete(`/patients/${p.id}`);
      toast.success("Paziente eliminato");
      await load(query.trim());
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const editCfCheck = validateCodiceFiscale(editForm.codice_fiscale, editForm.nominativo, editForm.nato_il, editForm.nato_a);

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <div className="mb-6">
          <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
            <Contact className="h-7 w-7 text-primary" /> Anagrafica Pazienti
          </h1>
          <p className="text-sm text-slate-500 mt-1">Pazienti trasportati salvati automaticamente. Cerca e consulta lo storico dei trasporti.</p>
        </div>

        <div className="relative mb-4 max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="patients-search" placeholder="Cerca per nominativo o telefono..."
            value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" />
        </div>

        <Card className="border-slate-200 overflow-hidden">
          {patients === null ? (
            <div className="p-5 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-12 w-full" />)}</div>
          ) : patients.length === 0 ? (
            <div className="py-16 text-center">
              <Contact className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">{query ? "Nessun paziente corrisponde alla ricerca." : "Nessun paziente in anagrafica."}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nominativo</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Codice Fiscale</TableHead>
                  <TableHead>Anni</TableHead>
                  <TableHead>Peso</TableHead>
                  <TableHead>Trasporti</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patients.map((p) => (
                  <TableRow key={p.id} data-testid={`patient-row-${p.id}`}>
                    <TableCell className="text-sm">
                      <p className="font-medium text-slate-800">{p.nominativo}</p>
                      {(p.nato_a || p.nato_il || p.indirizzo) && (
                        <p className="text-xs text-slate-400">
                          {patientSubtitle(p)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{p.telefono || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.email || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 font-mono text-xs">{p.codice_fiscale || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.anni || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.peso ? `${p.peso} kg` : "—"}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                        {p.trasporti}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit && (
                          <Button data-testid={`patient-edit-${p.id}`} size="sm" variant="ghost" className="gap-1.5"
                            onClick={() => openEdit(p)}>
                            <Pencil className="h-4 w-4" /> Modifica
                          </Button>
                        )}
                        <Button data-testid={`patient-history-${p.id}`} size="sm" variant="outline" className="gap-1.5"
                          onClick={() => openHistory(p)}>
                          <History className="h-4 w-4" /> Storico
                        </Button>
                        {canEdit && (
                          <Button data-testid={`patient-delete-${p.id}`} size="icon" variant="ghost"
                            className="h-8 w-8 text-slate-400 hover:text-red-600" title="Elimina paziente"
                            onClick={() => removePatient(p)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Dialog open={!!selected} onOpenChange={(o) => { if (!o) closeHistory(); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading" data-testid="patient-history-title">Storico — {selected?.nominativo}</DialogTitle>
              <DialogDescription>{selected?.telefono || "Nessun telefono"} · {selected?.trasporti ?? 0} trasporti totali{canEdit && ` · ${preventivi.length} preventivi`}</DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-auto space-y-5" data-testid="patient-history-body">
              {!loadingHistory && canEdit && <PatientPreventivi items={preventivi} onOpen={(id) => navigate(`/operativa/amministrazione/preventivi/${id}`)} />}
              <p className="nx-label">Trasporti</p>
              {loadingHistory ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : !history || history.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">Nessun trasporto trovato per questo paziente.</p>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => (
                    <button key={h.id} data-testid={`patient-trip-${h.id}`}
                      onClick={() => navigate(`/operativa/richiesta/${h.id}`)}
                      className="w-full text-left rounded-md border border-slate-200 px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3">
                      <span className="font-mono text-xs font-bold text-primary w-24">{h.code}</span>
                      <span className="text-sm text-slate-700 flex-1 flex items-center gap-1.5">
                        {h.partenza?.citta || "—"} <ArrowRight className="h-3 w-3 text-slate-400" /> {h.arrivo?.citta || "—"}
                      </span>
                      <span className="text-xs text-slate-500 w-32">{formatDateTime(h.operative?.data_ora_evento) || "—"}</span>
                      <StatusBadge status={h.status} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifica Paziente</DialogTitle>
              <DialogDescription>Correggi i dati anagrafici. Le richieste collegate verranno aggiornate.</DialogDescription>
            </DialogHeader>
            <form onSubmit={saveEdit} className="space-y-4 pt-2">
              <div>
                <Label className="nx-label">Nominativo</Label>
                <Input data-testid="edit-patient-nominativo" required className="mt-1.5"
                  value={editForm.nominativo} onChange={(e) => setEditForm({ ...editForm, nominativo: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="nx-label">Anni</Label>
                  <Input data-testid="edit-patient-anni" className="mt-1.5" type="number"
                    value={editForm.anni} onChange={(e) => setEditForm({ ...editForm, anni: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Peso (kg)</Label>
                  <Input data-testid="edit-patient-peso" className="mt-1.5"
                    value={editForm.peso} onChange={(e) => setEditForm({ ...editForm, peso: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="nx-label">Recapito Telefonico</Label>
                <Input data-testid="edit-patient-telefono" className="mt-1.5"
                  value={editForm.telefono} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} />
              </div>
              <div>
                <Label className="nx-label">Indirizzo Email</Label>
                <Input data-testid="edit-patient-email" type="email" className="mt-1.5"
                  value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div>
                <Label className="nx-label">Indirizzo</Label>
                <Input data-testid="edit-patient-indirizzo" className="mt-1.5"
                  value={editForm.indirizzo} onChange={(e) => setEditForm({ ...editForm, indirizzo: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="nx-label">Nato a</Label>
                  <Input data-testid="edit-patient-nato-a" className="mt-1.5"
                    value={editForm.nato_a} onChange={(e) => setEditForm({ ...editForm, nato_a: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Nato il</Label>
                  <Input data-testid="edit-patient-nato-il" className="mt-1.5" placeholder="gg/mm/aaaa"
                    value={editForm.nato_il} onChange={(e) => { const v = e.target.value; const age = ageFromBirthDate(v); setEditForm({ ...editForm, nato_il: v, anni: age || editForm.anni }); }} />
                </div>
              </div>
              <div>
                <Label className="nx-label">Codice Fiscale</Label>
                <Input data-testid="edit-patient-cf" className="mt-1.5"
                  value={editForm.codice_fiscale} onChange={(e) => setEditForm({ ...editForm, codice_fiscale: e.target.value })} />
                {editCfCheck && (
                  <p data-testid="edit-patient-cf-validation" className={`text-xs mt-1 ${editCfCheck.level === "ok" ? "text-emerald-600" : editCfCheck.level === "warn" ? "text-amber-600" : "text-red-600"}`}>
                    {editCfCheck.message}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="edit-patient-save" disabled={saving} className="gap-2 w-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva Modifiche"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
